import { Link } from "react-router-dom";
import {
  Printer, Download, ArrowLeft, Droplets, Waves, Layers, Sprout,
  TrendingUp, Landmark, ShieldCheck, CheckCircle2,
} from "lucide-react";
import { IMAGES, COMPANY } from "../lib/data";

const PRODUCTS = [
  { icon: Droplets, name: "BoneDry", cat: "Absorbents", role: "Keeps hydrocarbons out of the soil",
    desc: "Encapsulates oil & fuel on contact so spills never leach into the ground — leaving clean, uncontaminated earth." },
  { icon: Waves, name: "Pinkwater", cat: "Water Treatment", role: "Protects the water the land depends on",
    desc: "Treats problem water on site so contaminants never reach the watershed — safeguarding the aquifer and soil chemistry." },
  { icon: Layers, name: "Big Bad Mats", cat: "Ground Protection", role: "Preserves soil structure & topsoil",
    desc: "Distributes heavy loads to prevent rutting and compaction — the ground recovers and stays productive." },
];

const WHY = [
  { icon: TrendingUp, t: "Demand outpaces supply", d: "Capital is acquiring land for ag & livestock as demand outruns acreage." },
  { icon: Sprout, t: "Today's site, tomorrow's asset", d: "The ground under today's projects becomes rich, working land next cycle." },
  { icon: Landmark, t: "Condition sets value", d: "Healthy land vs. deteriorated/contaminated land is the difference in worth." },
];

export default function ResourcePreservationFlyer() {
  return (
    <div className="min-h-screen bg-charcoal/20 print:bg-white">
      <div className="sticky top-0 z-40 bg-ink text-white border-b border-white/10 print:hidden">
        <div className="max-w-6xl mx-auto px-6 h-14 flex items-center justify-between">
          <Link to="/land-stewardship" className="inline-flex items-center gap-2 text-white/70 hover:text-flame text-sm">
            <ArrowLeft className="h-4 w-4" /> Back to stewardship
          </Link>
          <div className="flex items-center gap-3">
            <span className="hidden sm:inline text-[11px] uppercase tracking-widest text-white/50">Resource Preservation · One-Page Flyer</span>
            <a href="/resource-preservation-flyer.pdf" download data-testid="download-rp-flyer" className="inline-flex items-center gap-2 border border-white/25 text-white px-4 py-2 text-sm font-semibold hover:border-flame hover:text-flame transition-colors">
              <Download className="h-4 w-4" /> Download PDF
            </a>
            <button onClick={() => window.print()} data-testid="print-rp-flyer" className="inline-flex items-center gap-2 bg-flame text-white px-4 py-2 text-sm font-semibold hover:bg-flame/90 transition-colors">
              <Printer className="h-4 w-4" /> Print
            </button>
          </div>
        </div>
      </div>

      <div className="py-8 print:py-0 flex justify-center">
        <div data-testid="rp-flyer-sheet" className="bg-white shadow-2xl print:shadow-none w-[297mm] h-[210mm] p-[11mm] flex flex-col" style={{ fontSize: "10px" }}>
          {/* header */}
          <header className="flex items-start justify-between border-b-2 border-ink pb-3">
            <div className="max-w-[66%]">
              <div className="flex items-center gap-2 mb-1.5">
                <img src={IMAGES.markDark} alt="OneHarvest" className="h-6 w-6 object-contain" />
                <span className="font-display font-extrabold tracking-tight text-ink text-sm">ONEHARVESTCO.</span>
                <span className="ml-2 border border-flame/40 bg-flame/10 text-flame px-2 py-0.5 text-[8px] uppercase tracking-[0.18em]">Resource Preservation Solution · Land Stewardship</span>
              </div>
              <h1 className="font-display font-black tracking-tight text-ink leading-[1] text-[34px]">
                Protect the land. Preserve the asset.
              </h1>
              <p className="mt-2 text-charcoal leading-snug text-[11px]">
                Every project sits on ground that will outlive it. <span className="font-semibold text-ink">BoneDry, Pinkwater, and Big Bad Mats</span> solve
                today's operational problem while keeping the land clean, intact, and productive — a resource landowners and operators both win on.
              </p>
            </div>
            <div className="w-[31%] flex flex-col items-end gap-2">
              <div className="w-full bg-ink text-white p-3 text-right">
                <div className="text-[8px] uppercase tracking-[0.18em] text-white/50">Land is a long-cycle asset</div>
                <div className="font-display font-extrabold text-flame text-[22px] leading-none mt-1">Steward it</div>
                <div className="text-[8px] text-white/50 mt-1">Risk management · resource preservation</div>
              </div>
            </div>
          </header>

          {/* products */}
          <div className="pt-4">
            <div className="text-[8px] uppercase tracking-[0.18em] text-flame mb-2">One portfolio, one mission — every product protects the resource</div>
            <div className="grid grid-cols-3 gap-4">
              {PRODUCTS.map((p) => (
                <div key={p.name} data-testid={`rp-product-${p.name}`} className="border border-border flex flex-col">
                  <div className="bg-ink text-white px-4 py-2.5 flex items-center gap-2.5">
                    <p.icon className="h-6 w-6 text-flame flex-shrink-0" />
                    <div>
                      <div className="text-[7.5px] uppercase tracking-[0.16em] text-flame">{p.cat}</div>
                      <div className="font-display font-bold text-[15px] leading-tight">{p.name}</div>
                    </div>
                  </div>
                  <div className="p-4 flex-1">
                    <div className="font-display font-bold text-ink text-[11px] leading-snug">{p.role}</div>
                    <p className="mt-1.5 text-charcoal text-[9.5px] leading-snug">{p.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* why it matters + dual value */}
          <div className="grid grid-cols-3 gap-4 pt-4 flex-1 min-h-0">
            {/* why */}
            <div className="border border-border p-4 flex flex-col">
              <div className="text-[8px] uppercase tracking-[0.18em] text-flame mb-2.5">Why it matters now</div>
              <div className="space-y-3">
                {WHY.map((w) => (
                  <div key={w.t} className="flex items-start gap-2.5">
                    <w.icon className="h-4 w-4 text-flame flex-shrink-0 mt-[1px]" />
                    <div>
                      <div className="text-[9.5px] font-semibold text-ink leading-snug">{w.t}</div>
                      <div className="text-[9px] text-charcoal leading-snug">{w.d}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* landowners */}
            <div className="border border-border p-4 flex flex-col">
              <div className="flex items-center gap-2 mb-2.5">
                <Landmark className="h-4 w-4 text-flame" />
                <div className="text-[8px] uppercase tracking-[0.18em] text-flame">For Landowners</div>
              </div>
              <p className="text-charcoal text-[9.5px] leading-snug">Healthy, uncontaminated ground lets you fully leverage your land for premium ag & livestock in a market where demand is outrunning supply.</p>
              <div className="mt-3 space-y-1.5">
                {["Protect soil, topsoil & the water table", "Preserve resale & lease value for future cycles", "Avoid remediation cost & contamination stigma"].map((t) => (
                  <div key={t} className="flex items-start gap-2"><CheckCircle2 className="h-3.5 w-3.5 text-flame flex-shrink-0 mt-[1px]" /><span className="text-charcoal text-[9px] leading-snug">{t}</span></div>
                ))}
              </div>
            </div>

            {/* operators */}
            <div className="border border-flame/40 bg-flame/[0.03] p-4 flex flex-col">
              <div className="flex items-center gap-2 mb-2.5">
                <TrendingUp className="h-4 w-4 text-flame" />
                <div className="text-[8px] uppercase tracking-[0.18em] text-flame">For Operators</div>
              </div>
              <p className="text-charcoal text-[9.5px] leading-snug">Turn environmental management into a value-add and visionary leadership your organization can own — strengthening relationships and reducing liability.</p>
              <div className="mt-3 space-y-1.5">
                {["Add value & visionary leadership to your brand", "Strengthen landowner trust & future access", "Reduce liability & support ESG mandates"].map((t) => (
                  <div key={t} className="flex items-start gap-2"><CheckCircle2 className="h-3.5 w-3.5 text-flame flex-shrink-0 mt-[1px]" /><span className="text-charcoal text-[9px] leading-snug">{t}</span></div>
                ))}
              </div>
            </div>
          </div>

          {/* footer */}
          <div className="mt-3 pt-2 border-t border-border flex items-center justify-between gap-4">
            <p className="text-[8px] text-muted-foreground leading-tight max-w-[62%]">
              Informational overview of OneHarvestCo. resource-preservation solutions (BoneDry, Pinkwater, Big Bad Mats). Outcomes depend on site conditions, application, and scope; not a contract, guarantee, or warranty of any specific environmental result. © {new Date().getFullYear()} {COMPANY.legal}.
            </p>
            <div className="flex items-center gap-4">
              <div className="text-right">
                <div className="font-display font-bold text-ink text-[12px] tracking-tight">Steward the land your operation runs on.</div>
                <div className="text-[9px] text-muted-foreground">{COMPANY.phone} · {COMPANY.email}</div>
              </div>
              <Link to="/request-quote" className="inline-flex items-center bg-flame text-white px-4 py-2.5 font-semibold text-[11px] hover:bg-flame/90 transition-colors print:hidden">
                Build a Plan
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
